import React from "react";
import MenuBar from "./MenuPanel";
import FooterPanel from "./FooterPanel";
import { useTranslation } from 'react-i18next';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation('footer');

  return (
    <React.Fragment>
      {/* Menu */}
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      {/* Title Section with Overlay */}
      <section className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">{t('privacyPolicy')}</h1>
          </div>
        </div>
      </section>

      {/* Main Content - Privacy Policy */}
      <main className="container-fluid px-3 px-md-5 py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-9">
            
            {/* PDF Display for Desktop */}
            <div className="d-none d-lg-block pdf-container" 
              style={{ 
                height: "calc(100vh - 150px)",
                minHeight: "600px",
                maxHeight: "1000px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                overflow: "auto"
              }}
            >
              <iframe
                src={`${process.env.PUBLIC_URL}/policy/Privacy Policy NewsLetter.pdf`}
                width="100%"
                height="100%"
                title={t('privacyPolicy')}
                style={{ border: "none", display: "block" }}
              />
            </div>

            {/* Formatted Content for Mobile */}
            <div className="privacy-content bg-white p-4 p-md-5 rounded shadow-sm d-lg-none">
              
              <h1 className="h2 text-center mb-5">Privacy Policy</h1>

              {/* Section 1 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">What is Grace Anointing Catholic Ministry doing about GDPR?</h2>
                <p>
                  Grace Anointing Catholic Ministry is committed to protecting your personal data in accordance 
                  with applicable data protection laws, including GDPR. We prioritize privacy and transparency in 
                  how we manage the data entrusted to us.
                </p>
                <p>
                  We treat the personal data entrusted to us with the utmost care and respect, ensuring it is used 
                  solely for the purposes of providing our ministry services, such as prayer requests and 
                  newsletters. Our approach to GDPR helps us maintain trust and accountability with our members.
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">What do I need to do to be GDPR compliant?</h2>
                <p>
                  Every ministry or organization is unique, and GDPR compliance obligations vary depending on 
                  how you choose to collect, use, and share data about your members or subscribers. You should 
                  evaluate your unique circumstances. To help you assess whether our ministry services align with 
                  your compliance needs, we recommend reviewing:
                </p>
                <ul className="mb-3">
                  <li>Our Terms of Service, Global Privacy Statement, and Data Processing Agreement(s), which 
                  outline how we handle any personal information you entrust to us (these will soon be updated 
                  as part of our GDPR Readiness program);</li>
                  <li>Our technical and organizational security measures;</li>
                  <li>How you may take actions in our systems to fulfill individual rights requests you may receive.</li>
                </ul>
              </section>

              {/* Section 3 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">Is Grace Anointing Catholic Ministry a controller or a processor?</h2>
                <p>
                  Grace Anointing Catholic Ministry acts as the controller of personal data provided by our 
                  members, meaning we determine how and why your data is used to support our ministry services, 
                  such as newsletters and prayer requests.
                </p>
                <p>
                  Under GDPR, a controller determines the "purposes and means" of data processing — in other 
                  words, "how and why" personal data is processed. A processor handles personal data solely on 
                  behalf of a controller.
                </p>
                <p>
                  Our mission is to serve and uplift our members and subscribers through spiritual guidance and 
                  community support. To fulfill this mission, we may use data to improve our ministry programs and 
                  communication efforts. For example, we might analyze subscription engagement to enhance our 
                  newsletters or community outreach.
                </p>
                <p>
                  Because we determine how and why data is processed to support members' spiritual growth and 
                  ministry services, we are considered a controller under GDPR. Recognizing this role means we 
                  take on significant responsibilities under GDPR, and we process all data in accordance with our 
                  Data Stewardship Principles to protect your privacy.
                </p>
              </section>

              {/* Section 4 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">Your Rights Under GDPR</h2>
                <p>Under GDPR, you have several rights regarding your personal data. These include:</p>
                <ul className="mb-3">
                  <li><strong>The right to access:</strong> You can request a copy of your personal data that we hold.</li>
                  <li><strong>The right to rectification:</strong> You can request correction of any inaccurate or incomplete data.</li>
                  <li><strong>The right to erasure (right to be forgotten):</strong> You can request that we delete your personal data 
                  under certain circumstances, especially if it is no longer necessary for the purposes for which 
                  it was collected.</li>
                  <li><strong>The right to restriction of processing:</strong> You can request that we limit the processing of your 
                  data.</li>
                  <li><strong>The right to object:</strong> You can object to the use of your data for prayer requests or newsletters 
                  at any time.</li>
                </ul>
                <p>
                  To exercise any of these rights, please contact us through the contact details provided on our 
                  website or directly via email.
                </p>
              </section>

              {/* Section 5 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">Will you sign my organization's Data Processing Agreement?</h2>
                <p>
                  As a religious ministry, we may use third-party service providers to assist in delivering our 
                  newsletter or managing prayer requests. In such cases, we ensure that these providers follow our 
                  instructions and comply with GDPR requirements to protect your personal data. Our third-party 
                  providers are carefully chosen and bound by contracts that ensure they handle your personal data 
                  appropriately and securely.
                </p>
                <p>
                  By subscribing to our newsletter or submitting a prayer request, you consent to us using your 
                  personal data for those specific purposes. You are not required to provide personal data to use 
                  any general information on our website, but if you choose to submit a prayer request or receive 
                  personalized prayers or newsletter communications, we will process your data in accordance with 
                  your consent. You may withdraw your consent at any time by contacting us or by unsubscribing 
                  from our newsletter.
                </p>
              </section>

              {/* Section 6 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">Where is my data located when I use your ministry services?</h2>
                <p>
                  We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy, 
                  such as responding to prayer requests or providing ministry-related communications. Personal 
                  data will not be retained for longer than needed. For example, if you request prayers, we will retain 
                  the request until the prayer has been offered or the purpose has been fulfilled. After this period, 
                  data will be securely deleted or anonymized, as applicable. For more information on our data 
                  retention practices, please contact us directly.
                </p>
                <p>
                  While we are based in USA, we may use third-party services located outside the European Union 
                  or the United Kingdom to manage prayer requests or distribute newsletters. If personal data is 
                  transferred to non-EU/UK locations, we ensure that adequate safeguards are in place to protect 
                  your data. This includes relying on approved data transfer mechanisms, such as EU Standard 
                  Contractual Clauses. If you have concerns about how your data is being transferred or protected, 
                  please contact us.
                </p>
                <p>
                  Our data is primarily stored in the USA, but as a global ministry, it may be accessed by our team 
                  and trusted partners from various locations. We ensure that any data transfers comply with 
                  GDPR's data protection requirements.
                </p>
                <p>
                  GDPR allows personal data from the EU or UK to be stored or otherwise processed in the USA, 
                  as long as there is an approved data transfer mechanism in place. We generally rely on the EU 
                  and UK-approved contractual clauses for enabling data transfers. We ensure that any data 
                  transferred outside the EU/UK is protected according to GDPR standards.
                </p>
                <p>
                  We also ensure that any trusted service providers we engage follow our instructions and maintain 
                  strong security measures to protect the data you entrust to us.
                </p>
              </section>

              {/* Section 7 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">We are a ministry. What is the protection for my members and subscribers?</h2>
                <p>
                  We implement a range of security measures to protect your data, including access controls, 
                  antivirus software, and regular risk assessments. These measures are designed to safeguard your 
                  information while ensuring compliance with data protection laws.
                </p>
                <p>
                  While no platform is without risk, we work diligently to ensure our systems and practices respect 
                  the privacy of our members and subscribers.
                </p>
                <p>
                  If you have any questions about how we protect your data or would like more information on our 
                  security practices, please contact us directly.
                </p>
              </section>

              {/* Section 8 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">Do you have an appointed Data Protection Officer?</h2>
                <p>
                  Yes, if you have any privacy-related questions, you can contact our Data Protection Officer 
                  through the contact form on our website or by email.
                </p>
              </section>

              {/* Section 9 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">Don't I have to keep my members' records for 7 years?</h2>
                <p>
                  We will retain your data only for as long as necessary to fulfill the purposes for which it was 
                  collected, such as responding to prayer requests or sending ministry newsletters. Data that is no 
                  longer needed will be securely deleted.
                </p>
              </section>

              {/* Section 10 */}
              <section className="mb-5">
                <h2 className="h4 text-primary mb-3">Updates to the policy</h2>
                <p>
                  From time to time, we may update this privacy policy to reflect changes in how we handle 
                  personal data or changes in the law. Any updates will be communicated to you via email or other 
                  communication methods, and the updated policy will be posted on our website. We encourage 
                  you to periodically review the policy to stay informed on how we protect your personal data.
                </p>
              </section>

              {/* Download PDF Option */}
              <section className="text-center mt-5 pt-4 border-top">
                <p className="text-muted mb-3">Download the complete privacy policy:</p>
                <a 
                  href={`${process.env.PUBLIC_URL}/policy/Privacy Policy NewsLetter.pdf`}
                  className="btn btn-primary"
                  download="Privacy_Policy_Newsletter.pdf"
                >
                  <i className="fas fa-file-pdf me-2"></i>Download PDF Version
                </a>
              </section>

            </div>

            {/* Download Button for Desktop (below PDF) */}
            <div className="text-center mt-4 d-none d-lg-block">
              <a 
                href={`${process.env.PUBLIC_URL}/policy/Privacy Policy NewsLetter.pdf`}
                className="btn btn-outline-primary"
                download="Privacy_Policy_Newsletter.pdf"
              >
                <i className="fas fa-download me-2"></i>Download PDF
              </a>
            </div>

          </div>
        </div>
      </main>

      {/* Footer */}
      <FooterPanel />
    </React.Fragment>
  );
};

export default PrivacyPolicyPage;
